import type { IAddressApi } from "../interfaces/IAddressApi";
import type { AddressInput, AddressSuggestionDto, AddressVerifyDto, LocalityDto } from "../../shared/AddressDtos";

// Adress-Assistent mit kleinem Cache (PLZ-Abfragen wiederholen sich beim Tippen)
export class AddressService {
  readonly api: IAddressApi;
  private readonly localityCache = new Map<string, LocalityDto[]>();

  constructor(api: IAddressApi) {
    this.api = api;
  }

  static supportsLookup(countryCode: string): boolean {
    return ["CH", "DE", "AT", "LI"].includes((countryCode || "").toUpperCase());
  }

  async localities(countryCode: string, zip: string): Promise<LocalityDto[]> {
    zip = zip.trim();
    if (zip.length < 4 || !AddressService.supportsLookup(countryCode)) {
      return [];
    }
    const key = `${countryCode}|${zip}`;
    const cached = this.localityCache.get(key);
    if (cached) {
      return cached;
    }
    try {
      const res = await this.api.localities(countryCode, zip);
      this.localityCache.set(key, res);
      return res;
    } catch {
      return [];
    }
  }

  async suggest(countryCode: string, query: string, zip: string, city: string, signal?: AbortSignal): Promise<AddressSuggestionDto[]> {
    if (query.trim().length < 2 || !AddressService.supportsLookup(countryCode)) {
      return [];
    }
    try {
      return await this.api.suggest(countryCode, query.trim(), zip.trim(), city.trim(), signal);
    } catch (err) {
      if ((err as Error).name === "AbortError") {
        throw err;
      }
      return [];
    }
  }

  async verify(address: AddressInput): Promise<AddressVerifyDto> {
    if (!AddressService.supportsLookup(address.countryCode) || address.streetName.trim() == "" || address.zipCode.trim() == "") {
      return { state: "notChecked", message: "", match: null };
    }
    try {
      return await this.api.verify(address);
    } catch {
      return { state: "unavailable", message: "", match: null };
    }
  }
}
